import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, MapPin, Calendar, Users, Loader2, ArrowRight, Clock } from 'lucide-react';
import { programsAPI, getImageUrl } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const categoryMap = {
  es: {
    Technology: 'Tecnologia',
    Healthcare: 'Salud',
    Sustainability: 'Sustentabilidad',
    Education: 'Educacion',
    Finance: 'Finanzas'
  }
};

const translateCategory = (category, language) => {
  if (language === 'es' && categoryMap.es[category]) return categoryMap.es[category];
  return category;
};

const getDaysRemaining = (deadline) => {
  const days = Math.ceil((new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
};

const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Small program card for the grid & recommendations
const ProgramCard = ({ program, language, t }) => {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString(language === 'es' ? 'es-CL' : 'en-US', { day: 'numeric', month: 'short' }) : '';
  const challengeText = stripHtml(program.challenge);

  return (
    <Link to={`/programs/${program._id}`} className="group block" data-testid={`program-card-${program._id}`}>
      <Card className="border border-gray-200 hover:border-purple-400 transition-all duration-300 hover:shadow-lg overflow-hidden h-full">
        <div className="h-44 overflow-hidden relative">
          <img
            src={getImageUrl(program.image)}
            alt={program.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {program.featured && (
            <div className="absolute top-3 left-3 px-2 py-1 text-xs font-semibold rounded" style={{ backgroundColor: '#ff9400', color: '#fff' }}>
              {language === 'es' ? 'DESTACADO' : 'FEATURED'}
            </div>
          )}
          <Badge
            className={`absolute top-3 right-3 ${
              program.status === 'open' ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'
            }`}
          >
            {program.status === 'open' ? t.status.open : t.status.closed}
          </Badge>
        </div>
        <CardContent className="p-4">
          <p className="text-xs font-medium text-purple-600 mb-1 uppercase tracking-wide">
            {program.organization}
          </p>
          <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-purple-700 transition-colors">
            {program.name}
          </h3>
          {challengeText && (
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{challengeText}</p>
          )}
          <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formatDate(program.startDate || program.deadline)} - {formatDate(program.deadline)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              <span>{program.applicants}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

// Large featured hero card
const FeaturedHero = ({ program, language, t }) => {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString(language === 'es' ? 'es-CL' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const challengeText = stripHtml(program.challenge);
  return (
    <Link to={`/programs/${program._id}`} className="group block" data-testid="featured-challenge-hero">
      <div className="relative rounded-xl overflow-hidden h-full min-h-[420px]">
        <img
          src={getImageUrl(program.image)}
          alt={program.name}
          className="w-full h-full object-cover absolute inset-0 group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute top-4 left-4">
          {program.featured && (
            <span className="px-3 py-1.5 text-xs font-bold rounded text-white" style={{ backgroundColor: '#ff9400' }}>
              {language === 'es' ? 'DESAFIO DESTACADO' : 'FEATURED CHALLENGE'}
            </span>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <p className="text-purple-300 text-sm font-medium mb-1">{program.organization}</p>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 group-hover:text-purple-200 transition-colors">
            {program.name}
          </h2>
          {challengeText && (
            <p className="text-gray-300 text-sm mb-4 line-clamp-3">{challengeText}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-300">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(program.startDate)} - {formatDate(program.deadline)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span>{program.applicants} {t.applicants}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{program.location}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { language } = useLanguage();
  const t = translations[language].programs;

  const categories = ['all', 'Technology', 'Healthcare', 'Sustainability', 'Education', 'Finance'];

  useEffect(() => {
    fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      const data = await programsAPI.getAll(params);
      setPrograms(data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPrograms = programs.filter((program) =>
    program.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    program.organization.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Separate featured and non-featured, with fallback to latest programs
  const trueFeatured = filteredPrograms.filter(p => p.featured && p.status === 'open');
  const hasFeatured = trueFeatured.length > 0;
  const heroSource = hasFeatured ? trueFeatured : filteredPrograms;
  const mainFeatured = heroSource[0] || null;
  const sideFeatured = hasFeatured ? trueFeatured.slice(1, 5) : filteredPrograms.slice(1, 4);
  const featuredIds = new Set(mainFeatured ? [mainFeatured._id, ...sideFeatured.map(p => p._id)] : []);
  const regularPrograms = filteredPrograms.filter(p => !featuredIds.has(p._id));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="py-12 px-4" style={{ backgroundColor: '#6949b6' }}>
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">{t.title}</h1>
          <p className="text-lg text-purple-200 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>
      </section>

      {/* Search + Categories */}
      <section className="bg-white border-b border-gray-200 py-4 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder={t.search}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-200 focus:border-purple-400"
                data-testid="program-search-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={
                    selectedCategory === category
                      ? 'text-white'
                      : 'border-gray-200 hover:border-purple-400 text-gray-700'
                  }
                  style={selectedCategory === category ? { backgroundColor: '#6949b6' } : {}}
                  data-testid={`category-filter-${category.toLowerCase()}`}
                >
                  {t.categories[category.toLowerCase()] || category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#6949b6' }} />
            </div>
          ) : (
            <>
              {/* Featured Section (Kickstarter-style) */}
              {mainFeatured && (
                <div className="mb-12">
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Big featured card - 3 cols */}
                    <div className="lg:col-span-3">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                        {hasFeatured
                          ? (language === 'es' ? 'DESAFIO DESTACADO' : 'FEATURED CHALLENGE')
                          : (language === 'es' ? 'DESAFIO PRINCIPAL' : 'TOP CHALLENGE')}
                      </p>
                      <FeaturedHero program={mainFeatured} language={language} t={t} />
                    </div>
                    {/* Side recommendations - 2 cols */}
                    <div className="lg:col-span-2">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                        {language === 'es' ? 'RECOMENDACIONES' : 'RECOMMENDATIONS'}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                        {(sideFeatured.length > 0 ? sideFeatured : regularPrograms.slice(0, 3)).map((program) => (
                          <ProgramCard key={program._id} program={program} language={language} t={t} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Count */}
              <div className="mb-6 flex items-center justify-between">
                <p className="text-gray-600">
                  {t.showing} <span className="font-semibold text-gray-900">{filteredPrograms.length}</span> {t.programs}
                </p>
              </div>

              {/* All Programs Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="programs-grid">
                {(mainFeatured ? regularPrograms : filteredPrograms).map((program) => (
                  <ProgramCard key={program._id} program={program} language={language} t={t} />
                ))}
              </div>

              {filteredPrograms.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-xl text-gray-500">{t.noResults}</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Programs;
